import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Avatar,
  Box,
  Button,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { trackEvent } from "src/Amplitude";
import CountryOrCryptoFlag from "src/components/common/CountryOrCryptoFlag";
import {
  CloseButton,
  Header,
  HeaderAndSearch,
  SearchBar,
  SearchBarIcon,
  SearchBarWrapper,
  Wrapper,
} from "src/components/common/crypto-selection-modalV2/CryptoSelectionModalV2";
import { TargetOrderHoldingsSkeleton } from "src/components/common/Skeletons";
import MagnifyingGlass from "src/icons/MagnifyingGlass";
import api from "src/store/features/api";
import {
  SelectedStateType,
  setCompletedFormSection,
  setSelectedToken,
  useTargetState,
} from "src/store/features/TargetOrders.slice";
import { useAppDispatch } from "src/store/store";
import { dynamicUnitFormatter } from "src/utils/helpers/PriceAndUnitsHelpers";
import { TargetOrderStyles } from "../../styles/TargetOrder.style";
import { NoHoldings } from "./TargetTools";

type SellDropDownProps = {
  userDefaultCurrency?: any;
};

export const TargetOrderUserHoldingsModal = ({ userDefaultCurrency }: SellDropDownProps) => {
  //----------------------REDUX---------------------
  const {
    data: userHoldings,
    refetch,
    isLoading,
  } = api.useGetWalletPageHoldingsQuery({
    baseCurrency: userDefaultCurrency,
  });
  const { selectedToken } = useTargetState();
  const dispatch = useAppDispatch();
  //----------------------Styles---------------------
  const classes = TargetOrderStyles();
  const theme = useTheme();
  //----------------------State---------------------
  const [modal, setModal] = useState(false);
  const [searchVal, setSearchVal] = useState("");

  //----------------------CONSTS---------------------

  const treatedHoldings = userHoldings
    ?.filter(
      (item) =>
        item.currencyConfig.type === "Crypto" &&
        item.currencyConfig.secondarySector !== "Stablecoin",
    )
    .filter((el) => el.currencyConfig.name?.toLowerCase().includes(searchVal.toLocaleLowerCase()))
    .filter((el) => el.available !== 0)
    .filter((item) => item.available > 0);

  //----------------------Hooks---------------------

  useEffect(() => {
    refetch();
  }, []);
  //----------------------Functions---------------------
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchVal(e.target.value);
  };

  const handleCoinSelect = (event: React.MouseEvent, selectedCurrency: SelectedStateType) => {
    const body: SelectedStateType = {
      icon: selectedCurrency.icon,
      code: selectedCurrency.code,
      available: selectedCurrency.available,
      balance: selectedCurrency.balance,
      units: selectedCurrency.units,
      price: selectedCurrency.price,
      type: selectedCurrency.type,
      value: selectedCurrency.value,
      selected: true,
    };
    dispatch(setCompletedFormSection("tokenInput"));
    dispatch(setSelectedToken(body));
    handleModalToggle();
    trackEvent(`Target_Order_User_Sell_Token_Modal`, {
      Selected_coin: selectedCurrency.code,
    });
  };
  const handleModalToggle = () => {
    // setSearchVal("");
    setModal((prev) => !prev);
  };

  return (
    <>
      <Button
        disableRipple
        id="token-select-btn-sell"
        sx={{
          height: "33px",
          borderRadius: "10px",
          padding: "8px",
          width: "auto",
          alignItems: "center",
          justifyContent: "normal !important",
          backgroundColor: selectedToken.code ? "#E8F6FF" : theme.palette.primary.main,

          "&:hover": { backgroundColor: " #4e7086" },
        }}
        onClick={(e) => {
          handleModalToggle();
        }}>
        <Stack direction="row" justifyContent="center" alignItems="center">
          {selectedToken.code && (
            <Avatar style={{ backgroundColor: "transparent" }}>
              <CountryOrCryptoFlag name={selectedToken.icon || selectedToken.code} size={20} />
            </Avatar>
          )}
          <Typography
            sx={{
              color: selectedToken.code ? theme.palette.primary.main : "#fff",
              fontWeight: 600,
              lineHeight: "17px",
              fontSize: "14px",
              paddingLeft: " 5px",
              textTransform: "capitalize",
            }}>
            {selectedToken.code ? selectedToken.code : "Select Token"}
          </Typography>
          {!selectedToken.code && <ExpandMoreIcon sx={{ color: "#fff" }} />}
        </Stack>
      </Button>
      <Wrapper
        open={modal}
        onClose={handleModalToggle}
        sx={{ "& .MuiDialog-paper": { height: "fit-content" } }}>
        <HeaderAndSearch>
          {/* header */}
          <Header>
            {"Select Token To Sell"}
            {/* close button */}
            <CloseButton onClick={handleModalToggle}>
              <CloseIcon fontSize="inherit" />
            </CloseButton>
          </Header>
          <SearchBarWrapper>
            <SearchBar
              autoFocus
              placeholder="Search"
              onChange={handleSearchChange}
              value={searchVal}
            />
            <SearchBarIcon>
              <MagnifyingGlass />
            </SearchBarIcon>
          </SearchBarWrapper>
        </HeaderAndSearch>

        <Grid container>
          <List className={classes.list} id="sell-token-list">
            {isLoading && <TargetOrderHoldingsSkeleton />}
            {!treatedHoldings?.length && !isLoading && (
              <Box sx={{ paddingRight: "10px", paddingLeft: "10px" }}>
                <NoHoldings origin="target-sell" />
              </Box>
            )}
            {treatedHoldings &&
              treatedHoldings.map((el: any, idx: any) => (
                <Button
                  disableRipple
                  id={`select-sell-token-${el.currencyConfig.code}`}
                  fullWidth
                  key={idx}
                  sx={{ maxHeight: "80px" }}
                  onClick={(event) =>
                    handleCoinSelect(event, {
                      icon: el?.currencyConfig.logoUrl,
                      code: el.currencyConfig.code,
                      balance: el.available,
                      units: el.total,
                      price: el.price,
                      type: el.currencyConfig.type,
                      available: el.available,
                      value: el.value,
                      selected: true,
                    })
                  }>
                  <Grid container>
                    <ListItem
                      data-code={el?.currencyConfig.code}
                      data-name={el?.currencyConfig.name}>
                      <ListItemAvatar>
                        <Avatar style={{ backgroundColor: "transparent" }}>
                          <CountryOrCryptoFlag
                            name={el?.currencyConfig.logoUrl || el?.currencyConfig.code}
                            size={35}
                          />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={el?.currencyConfig.name.replace(/\s*\(.*?\)\s*/g, "")}
                        primaryTypographyProps={{
                          fontSize: "14px",
                          fontWeight: 700,
                        }}
                        sx={{
                          textTransform: "none",
                          flexBasis: "auto",
                        }}
                      />

                      <ListItemText
                        sx={{
                          textTransform: "none",
                          textAlign: "end",
                        }}
                        primaryTypographyProps={{
                          fontSize: "14px",
                          fontWeight: 700,
                        }}
                        primary={
                          dynamicUnitFormatter(el.available, 0) + " " + el.currencyConfig.code
                        }
                      />
                    </ListItem>
                  </Grid>
                </Button>
              ))}
          </List>
        </Grid>
      </Wrapper>
    </>
  );
};
export default TargetOrderUserHoldingsModal;
